import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { http } from "../lib/api";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

/**
 * Lets an admin pick an image file → uploads directly to Cloudinary via a
 * server-signed POST → returns the secure CDN URL through onUploaded.
 *
 * Flow:
 *   1) ask backend /api/admin/cloudinary/signature for a short-lived signature
 *   2) POST file + signature + api_key + timestamp + folder to Cloudinary
 *   3) read secure_url from the response and bubble it up
 */
export default function ImageUploader({ folder = "posts/covers", onUploaded, label = "Upload image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Only JPG, PNG, or WebP images are allowed.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be 5 MB or smaller.");
      return;
    }

    setBusy(true);
    try {
      // 1. Ask our backend to sign the upload params.
      const { data: sig } = await http.get("/admin/cloudinary/signature", {
        params: { folder },
      });

      // 2. POST the file directly to Cloudinary (does NOT hit our backend).
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", String(sig.timestamp));
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: form }
      );
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const json = await res.json();
      if (!json.secure_url) throw new Error("Upload response missing secure_url");

      // Inject f_auto,q_auto so Cloudinary auto-serves WebP/AVIF and auto-picks
      // the best quality per device — typically 50–70% smaller, no visible loss.
      // Example:
      //   https://res.cloudinary.com/x/image/upload/v123/posts/covers/foo.png
      // →
      //   https://res.cloudinary.com/x/image/upload/f_auto,q_auto/v123/posts/covers/foo.png
      const optimized = json.secure_url.replace(
        "/image/upload/",
        "/image/upload/f_auto,q_auto/"
      );

      onUploaded?.(optimized);
      toast.success("Image uploaded.");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("[uploader] Cloudinary upload failed:", err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        className="inline-flex items-center gap-2 mt-2 px-3 py-2 border border-[#151e1a] text-[#151e1a] hover:bg-[#151e1a] hover:text-[#f8f4ec] transition-colors text-xs font-mono uppercase tracking-widest disabled:opacity-50"
        data-testid="image-upload-button"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {busy ? "Uploading…" : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
        data-testid="image-upload-input"
      />
    </>
  );
}
